import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * BarcodeScanModal — key-free camera scanner for asset labels.
 *
 * Decodes both QR codes and 1D barcodes (Code 128, EAN, etc.) from the device
 * camera using ZXing — the multi-format counterpart to the dormant AI OCR path.
 * It needs no API key and no external service: the browser's getUserMedia +
 * ZXing do everything locally. On a successful decode it calls onResult with the
 * decoded text (e.g. an asset tag or serial) and closes.
 *
 * Used by the Assets page: scan a printed asset label → the decoded value is
 * dropped into the search box → the matching device is pulled up.
 */
import { BrowserMultiFormatReader } from '@zxing/browser';
import { ScanLine, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
export function BarcodeScanModal({ onResult, onClose, }) {
    const videoRef = useRef(null);
    const controlsRef = useRef(null);
    const [error, setError] = useState(null);
    const [ready, setReady] = useState(false);
    useEffect(() => {
        let cancelled = false;
        const reader = new BrowserMultiFormatReader();
        (async () => {
            try {
                if (!navigator.mediaDevices?.getUserMedia) {
                    setError('This browser cannot access a camera. Type the tag/serial instead.');
                    return;
                }
                const controls = await reader.decodeFromVideoDevice(undefined, // default camera (prefers the rear camera on phones)
                videoRef.current, (result, _err, ctrl) => {
                    if (cancelled)
                        return;
                    if (!ready)
                        setReady(true);
                    if (result) {
                        const text = result.getText().trim();
                        if (text) {
                            ctrl.stop();
                            onResult(text);
                        }
                    }
                });
                if (cancelled) {
                    controls.stop();
                    return;
                }
                controlsRef.current = controls;
            }
            catch (e) {
                if (cancelled)
                    return;
                const name = e instanceof Error ? e.name : '';
                if (name === 'NotAllowedError' || name === 'SecurityError') {
                    setError('Camera access was blocked. Allow camera permission, or type the tag/serial.');
                }
                else if (name === 'NotFoundError') {
                    setError('No camera found on this device. Type the tag/serial instead.');
                }
                else {
                    setError('Could not start the camera. Type the tag/serial instead.');
                }
            }
        })();
        return () => {
            cancelled = true;
            controlsRef.current?.stop();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- run once: set up the scanner on mount, tear down on unmount
    }, []);
    return (_jsx(Modal, { open: true, onClose: onClose, title: "Scan asset label", size: "md", children: _jsxs("div", { className: "space-y-3", children: [_jsx("p", { className: "text-xs text-slate-500", children: "Point the camera at the QR code or barcode on the device's asset label. It scans automatically \u2014 no key or app needed." }), error ? (_jsx("div", { className: "rounded-md bg-amber-50 px-3 py-4 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-200", children: error })) : (_jsxs("div", { className: "relative overflow-hidden rounded-lg border border-slate-300 bg-black dark:border-slate-700", children: [_jsx("video", { ref: videoRef, className: "h-64 w-full object-cover" }), _jsx("div", { className: "pointer-events-none absolute inset-0 flex items-center justify-center", children: _jsx(ScanLine, { className: "h-16 w-16 text-emerald-400/70" }) }), !ready && (_jsx("div", { className: "absolute inset-0 flex items-center justify-center text-sm text-white/80", children: "Starting camera\u2026" }))] })), _jsx("div", { className: "flex justify-end", children: _jsxs(Button, { variant: "ghost", onClick: onClose, children: [_jsx(X, { size: 15 }), "Close"] }) })] }) }));
}
